import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface MaintenanceStatus {
  enabled: boolean;
  message: string;
}

export function useMaintenanceMode() {
  const [status, setStatus] = useState<MaintenanceStatus>({
    enabled: false,
    message: '',
  });
  const [isLoading, setIsLoading] = useState(true);

  const checkMaintenanceMode = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('system_config')
        .select('value')
        .eq('key', 'maintenance_mode')
        .single();

      if (error) {
        // If no record found, maintenance mode is off
        if (error.code === 'PGRST116') {
          setStatus({ enabled: false, message: '' });
        } else {
          console.error('Error checking maintenance mode:', error);
        }
      } else if (data?.value) {
        setStatus({
          enabled: data.value.enabled ?? false,
          message: data.value.message ?? '',
        });
      }
    } catch (err) {
      console.error('Error checking maintenance mode:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkMaintenanceMode();

    // Subscribe to changes in system_config
    const channel = supabase
      .channel('maintenance-mode-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'system_config',
          filter: 'key=eq.maintenance_mode',
        },
        (payload) => {
          if (payload.new && typeof payload.new === 'object' && 'value' in payload.new) {
            const value = payload.new.value as { enabled?: boolean; message?: string };
            setStatus({
              enabled: value?.enabled ?? false,
              message: value?.message ?? '',
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [checkMaintenanceMode]);

  return {
    isMaintenanceMode: status.enabled,
    maintenanceMessage: status.message,
    isLoading,
    refresh: checkMaintenanceMode,
  };
}
